import {useEffect, useRef} from 'react';

import {getCoverMaterialName, getCoverVariantProductName} from '@amblique/king-living/app/utils/customize-selections';
import {getStockLevel} from '@amblique/king-living/app/utils/cover-stock-display';
import {PDP_CONFIG_MODE_CUSTOMIZE} from '@amblique/king-living/app/utils/pdp-config-url';

/**
 * @param {Object} variant
 * @returns {boolean} True when the cover has tracked stock (in-stock / low-stock).
 *
 * Uses {@link getStockLevel} so warehouse-merged `linkedInventory` and pending
 * hydrate flags stay consistent with the swatch grid.
 */
function hasTrackedStock(variant) {
  const level = getStockLevel(variant);

  return level === 'in-stock' || level === 'low-stock';
}

/**
 * Build the canonical cover-selection shape consumed by `CustomizeJourneyContext`.
 * Mirrors the object produced by `CoverStep.handleVariantSelect` and
 * `buildDefaultSelectionsFromData` so downstream consumers (AddToCartStrip,
 * ComplexAddToCartButton, ReviewSection gallery) do not need Freeform-specific code.
 *
 * @param {Object} variant - Enriched variant entry from `c_allVariantsData`.
 * @returns {{productId: string, variationValues: Object, name: string, salePrice: number|null, price: number|null, imageUrl: string|null}}
 */
export function buildCoverSelectionFromVariant(variant) {
  return {
    productId: variant.productId,
    variationValues: variant.variationValues || {},
    name: getCoverVariantProductName(variant),
    materialName: getCoverMaterialName(variant) || null,
    salePrice: variant.salePrice ?? null,
    price: variant.price ?? null,
    imageUrl: variant.swatchImageUrl ?? null,
    selectedAt: Date.now(),
  };
}

/**
 * FSD §5.1 — on Simple PDP load, auto-select the first cover with tracked stock (ATS > 0).
 * CMTO masters (covers ATS N/A or 0) leave cover unselected (KINDW-1595).
 *
 * Skip when URL has `coverVariantId`, or when URL `mode` is set/customize and this PDP
 * has linked sets (or they are still loading) so hydrate owns cover.
 *
 * Fires at most once per master so later cover changes do not auto-select again.
 */
export function useFreeformCoverPreselect({
  enabled,
  variants,
  currentSelections,
  commitInlineSelections,
  productId,
  urlCoverVariantId = null,
  urlPdpMode = null,
  honorUrlJourneyMode = false,
  userClearedCoverRef,
}) {
  const didFireRef = useRef(false);
  const lastProductIdRef = useRef(null);

  useEffect(() => {
    if (lastProductIdRef.current !== productId) {
      lastProductIdRef.current = productId;
      didFireRef.current = false;
    }
  }, [productId]);

  useEffect(() => {
    if (!enabled) return;

    if (honorUrlJourneyMode && (urlPdpMode === 'set' || urlPdpMode === PDP_CONFIG_MODE_CUSTOMIZE)) {
      didFireRef.current = true;

      return;
    }

    if (urlCoverVariantId) {
      didFireRef.current = true;

      return;
    }

    if (didFireRef.current) return;
    if (userClearedCoverRef?.current) {
      didFireRef.current = true;

      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) return;
    if (typeof commitInlineSelections !== 'function') return;
    if (currentSelections?.cover?.productId) {
      didFireRef.current = true;

      return;
    }

    const firstInStock = variants.find(hasTrackedStock);

    if (!firstInStock) {
      // FSD §5.1 — leave no cover selected when nothing has tracked stock.
      didFireRef.current = true;

      return;
    }

    didFireRef.current = true;

    const baseSelections = currentSelections && typeof currentSelections === 'object' ?
      currentSelections :
      {};

    commitInlineSelections({
      ...baseSelections,
      cover: buildCoverSelectionFromVariant(firstInStock),
    });
  }, [enabled, variants, currentSelections, commitInlineSelections, urlCoverVariantId, urlPdpMode, honorUrlJourneyMode, userClearedCoverRef]);
}
